import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, type Dispatch, type SetStateAction } from "react"
import { usePersistedState } from "@/hooks/use-persisted-state"
import { usePerTabNavigationHistory } from "@/hooks/use-navigation-history"
import { StorageKeys, StorageSchemas } from "@/lib/storage"
import { openDocumentInNewWindow, getWindowState, cleanupWindowState } from "@/lib/window-manager"
import type { OpenTab } from "../features/document-tabs"
import type { ChatReference } from "./resizable-layout.types"
import type { UrlChangeParams } from "@/hooks/use-url-sync"
import { stripOptionalBasecampWorkspaceSegment } from "@/lib/basecamp-url"
import { parseAppPath, type WorkspaceRouteInfo } from "@/lib/app-routes"
import {
  applyColdLoadDocumentDeepLinkToTabState,
  captureColdLoadDocumentDeepLink,
  isBrowserReloadNavigation,
} from "@/lib/document-deep-link-cold-load"
import { isDocumentDeepLinkRestorePending } from "@/lib/document-deep-link-pending"
import {
  DEFAULT_RESIZABLE_PANEL_TRIPLE,
  dedupeOpenTabsById,
  generateTabSlotId,
} from "./resizable-layout.utils"
import { browserTabTitleFromActiveTab } from "@/lib/browser-tab-title"
import { BRAND } from "@/lib/config/brand"

/**
 * Hook to manage all ResizableLayout state
 */
export function useResizableLayoutState() {
  // Check if this is a new window with window state
  // Use useMemo to ensure these values are stable across renders
  const { windowId, windowState, storageKey, activeTabKey } = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const id = urlParams.get('windowId')
    const state = id ? getWindowState() : null
    const tabsKey = id
      ? StorageKeys.DOCUMENTS_OPEN_TABS_WINDOW(id)
      : StorageKeys.DOCUMENTS_OPEN_TABS
    const tabKey = id
      ? StorageKeys.DOCUMENTS_ACTIVE_TAB_WINDOW(id)
      : StorageKeys.DOCUMENTS_ACTIVE_TAB
    return {
      windowId: id,
      windowState: state,
      storageKey: tabsKey,
      activeTabKey: tabKey
    }
  }, []) // Empty deps - only compute once on mount

  // Address-bar document intent must win before first paint (every-frame: no Home flash).
  const coldDocumentDeepLink = useMemo(
    () => captureColdLoadDocumentDeepLink(window.location.pathname),
    [],
  )
  const coldDeepLinkAppliedRef = useRef(false)

  const [showSidebar, setShowSidebar] = usePersistedState({
    key: StorageKeys.UI_SHOW_SIDEBAR,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  })
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedState({
    key: StorageKeys.UI_SIDEBAR_COLLAPSED,
    defaultValue: false,
    schema: StorageSchemas.boolean,
  })
  const [sidebarAutoHide, setSidebarAutoHide] = usePersistedState({
    key: StorageKeys.UI_SIDEBAR_AUTO_HIDE,
    defaultValue: false,
    schema: StorageSchemas.boolean,
  })
  const [showAgent, setShowAgent] = usePersistedState({
    key: StorageKeys.UI_SHOW_AGENT,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  })
  const [agentCollapsed, setAgentCollapsed] = usePersistedState({
    key: StorageKeys.UI_AGENT_COLLAPSED,
    defaultValue: false,
    schema: StorageSchemas.boolean,
  })
  const [showDocumentList, setShowDocumentList] = usePersistedState({
    key: StorageKeys.UI_SHOW_DOCUMENT_LIST,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  })
  const [persistedOpenTabs, setPersistedOpenTabs] = usePersistedState<Array<OpenTab>>({
    key: storageKey,
    defaultValue: windowState?.tabState || [{
      id: "home",
      title: "Home",
      type: "home",
      isDirty: false,
      tabSlotId: "tab-main",  // Unified navigation history
    }],
    schema: StorageSchemas.openTabs,
  })
  const openTabs = useMemo(() => dedupeOpenTabsById(persistedOpenTabs), [persistedOpenTabs])
  const setOpenTabs = useCallback<Dispatch<SetStateAction<OpenTab[]>>>(
    (next) =>
      setPersistedOpenTabs((prev) =>
        dedupeOpenTabsById(typeof next === "function" ? next(dedupeOpenTabsById(prev)) : next),
      ),
    [setPersistedOpenTabs],
  )
  const [activeTabId, setActiveTabId] = usePersistedState<string | null>({
    key: activeTabKey,
    defaultValue: windowState?.activeTabId || windowState?.documentId || 'home',
  })
  // Optimistic tab bar state - updates synchronously on click for instant visual feedback
  const [tabBarActiveId, setTabBarActiveId] = useState<string | null>(
    () => coldDocumentDeepLink?.pathOrId ?? 'home',
  )

  // Force deep-link tab active before paint when localStorage still has Home / another doc.
  // Reload keeps the full open-tabs session; navigate (share paste / new tab) isolates.
  useLayoutEffect(() => {
    if (!coldDocumentDeepLink || coldDeepLinkAppliedRef.current) return
    coldDeepLinkAppliedRef.current = true
    const applied = applyColdLoadDocumentDeepLinkToTabState(
      openTabs,
      activeTabId,
      coldDocumentDeepLink,
      { preserveSession: isBrowserReloadNavigation() },
    )
    if (applied.activeTabId !== activeTabId) {
      setActiveTabId(applied.activeTabId)
    }
    if (applied.openTabs !== openTabs) {
      setOpenTabs(applied.openTabs)
    }
    setTabBarActiveId(applied.activeTabId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cold-load once; openTabs/activeTabId are initial hydrate only
  }, [coldDocumentDeepLink])
  const [selectedProjectId, setSelectedProjectId] = usePersistedState<string | null>({
    key: StorageKeys.DOCUMENTS_SELECTED_PROJECT,
    defaultValue: null,
  })

  // Track closed tabs for reopening (last 10 closed tabs)
  const [closedTabsHistory, setClosedTabsHistory] = useState<Array<OpenTab>>([])

  // Persisted panel sizes: [leftSidebar, mainContent, rightSidebar]
  // Default: see DEFAULT_RESIZABLE_PANEL_TRIPLE when all panels visible
  // ⚠️ CRITICAL: Schema validation ensures array length and sum ~100
  // This fixes the chat panel resize lock bug!
  const [panelSizes, setPanelSizes] = usePersistedState<number[]>({
    key: StorageKeys.UI_PANEL_SIZES,
    defaultValue: [...DEFAULT_RESIZABLE_PANEL_TRIPLE],
    schema: StorageSchemas.panelSizes,
  })

  // Store expanded sizes before collapsing (for restoration)
  const [expandedLeftSize, setExpandedLeftSize] = usePersistedState<number>({
    key: StorageKeys.UI_EXPANDED_LEFT_SIZE,
    defaultValue: DEFAULT_RESIZABLE_PANEL_TRIPLE[0],
    schema: StorageSchemas.number,
  })
  const [expandedRightSize, setExpandedRightSize] = usePersistedState<number>({
    key: StorageKeys.UI_EXPANDED_RIGHT_SIZE,
    defaultValue: DEFAULT_RESIZABLE_PANEL_TRIPLE[2],
    schema: StorageSchemas.number,
  })

  // Non-persisted state (resets on refresh)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [chatReferences, setChatReferences] = useState<Array<ChatReference>>([])
  const [activeDocumentPath, setActiveDocumentPath] = useState<string | null>(null)
  const [activeDocumentIdForFolderView, setActiveDocumentIdForFolderView] = useState<string | null>(null)

  // Navigation history (per-tab)
  const navigation = usePerTabNavigationHistory()

  // Sync tabBarActiveId when activeTabId changes from URL/history (not from tab click)
  useEffect(() => {
    setTabBarActiveId(activeTabId)
  }, [activeTabId])

  return {
    // Window state
    windowId,
    windowState,
    storageKey,
    activeTabKey,

    // UI state
    showSidebar,
    setShowSidebar,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarAutoHide,
    setSidebarAutoHide,
    showAgent,
    setShowAgent,
    agentCollapsed,
    setAgentCollapsed,
    showDocumentList,
    setShowDocumentList,

    // Tab state
    openTabs,
    setOpenTabs,
    activeTabId,
    setActiveTabId,
    tabBarActiveId,
    setTabBarActiveId,
    selectedProjectId,
    setSelectedProjectId,
    closedTabsHistory,
    setClosedTabsHistory,

    // Panel sizing
    panelSizes,
    setPanelSizes,
    expandedLeftSize,
    setExpandedLeftSize,
    expandedRightSize,
    setExpandedRightSize,

    // Dialog state
    commandPaletteOpen,
    setCommandPaletteOpen,
    settingsOpen,
    setSettingsOpen,
    keyboardShortcutsOpen,
    setKeyboardShortcutsOpen,
    isFullscreen,
    setIsFullscreen,

    // Document state
    chatReferences,
    setChatReferences,
    activeDocumentPath,
    setActiveDocumentPath,
    activeDocumentIdForFolderView,
    setActiveDocumentIdForFolderView,

    // Navigation
    navigation,
  }
}

/**
 * Hook to handle tab migration (fix old document-ID-based tabSlotIds)
 */
export function useTabMigration(
  openTabs: OpenTab[],
  setOpenTabs: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void,
  activeTabId?: string | null,
  setActiveTabId?: (id: string | null) => void
) {
  useEffect(() => {
    const needsExplorerIdMigration = openTabs.some(
      (tab) => tab.type === 'explorer' && /^tab-\d+$/.test(tab.id)
    )
    if (needsExplorerIdMigration) {
      let nextActiveId = activeTabId ?? null
      const migratedTabs = openTabs.map((tab) => {
        if (tab.type === 'explorer' && /^tab-\d+$/.test(tab.id)) {
          const explorerTabId = `explorer-${tab.id.replace('tab-', '')}`
          if (activeTabId === tab.id) nextActiveId = explorerTabId
          return { ...tab, id: explorerTabId }
        }
        return tab
      })
      setOpenTabs(migratedTabs)
      if (nextActiveId !== activeTabId && setActiveTabId) {
        setActiveTabId(nextActiveId)
      }
    }

    const needsMigration = openTabs.some(tab => {
      // Skip list tab
      if (!tab.tabSlotId || tab.tabSlotId === 'tab-main') return false;

      // Check if tabSlotId looks like a timestamp (all digits after 'tab-')
      const idPart = tab.tabSlotId.replace('tab-', '');
      const isTimestamp = /^\d+$/.test(idPart);

      return !isTimestamp; // Needs migration if NOT a timestamp
    });

    if (needsMigration) {
      const migratedTabs = openTabs.map((tab, index) => {
        if (tab.tabSlotId === 'tab-main') return tab; // Keep Explorer tab as-is

        const idPart = tab.tabSlotId?.replace('tab-', '') || '';
        const isTimestamp = /^\d+$/.test(idPart);

        if (!isTimestamp) {
          // Generate new timestamp-based tabSlotId with small delay to ensure uniqueness
          const newTabSlotId = generateTabSlotId();
          return { ...tab, tabSlotId: newTabSlotId };
        }

        return tab;
      });
      setOpenTabs(migratedTabs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount
}

/**
 * Hook to initialize navigation history for Explorer tab
 */
export function useExplorerNavigationInit(
  activeTabId: string | null,
  openTabs: OpenTab[],
  selectedProjectId: string | null,
  navigation: ReturnType<typeof usePerTabNavigationHistory>
) {
  useEffect(() => {
    const activeTab = openTabs.find(tab => tab.id === activeTabId)
    if (activeTab?.type === 'explorer') {
      const slotId = activeTab.tabSlotId || 'tab-main'
      const currentState = navigation.tabNavigationState[slotId]

      // Only initialize if history is completely empty
      if (!currentState || currentState.history.length === 0) {
        navigation.navigate(slotId, {
          documentId: null,
          folderPath: selectedProjectId,
          viewMode: 'list'
        }, "explorer-nav-init")
      }
    }
  }, [activeTabId, openTabs, navigation]) // Removed selectedProjectId from deps to prevent loops
}

/**
 * Hook to initialize navigation for list tab
 */
export function useListTabNavigationInit(
  navigation: ReturnType<typeof usePerTabNavigationHistory>
) {
  const didInit = useRef(false)
  useEffect(() => {
    if (didInit.current) return
    didInit.current = true
    navigation.navigate('tab-main', { documentId: null, folderPath: null, viewMode: 'list' }, "list-tab-nav-init")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

/**
 * Hook to handle window cleanup on unmount
 */
export function useWindowCleanup(windowId: string | null) {
  useEffect(() => {
    if (!windowId) return

    const handleBeforeUnload = () => {
      cleanupWindowState(windowId)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [windowId])
}

/**
 * Hook to restore tabs from URL on initial load
 * This ensures that when you reload a page with a Basecamp/document URL,
 * the appropriate tab is restored even if it's not in localStorage
 */
export function useUrlTabRestore(
  openTabs: OpenTab[],
  activeTabId: string | null,
  setOpenTabs: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void,
  setActiveTabId: (id: string | null) => void,
  onUrlChange: (contentId: string, params: UrlChangeParams) => void | Promise<void>
) {
  useEffect(() => {
    // Only run on initial mount (when openTabs is default/empty)
    const isInitialLoad = openTabs.length === 1 && openTabs[0]?.id === 'home' && activeTabId === 'home'
    
    if (!isInitialLoad) {
      return
    }

    // Check URL to see if we need to restore a tab
    const pathname = window.location.pathname
    const urlParams = new URLSearchParams(window.location.search)
    
    // /{workspaceSlug}/basecamp/project/... (canonical v2)
    const wsScopedBasecamp = pathname.match(
      /^\/([^/]+)\/basecamp\/project\/(\d+)(?:\/todo\/(\d+))?$/
    )
    if (wsScopedBasecamp) {
      const [, , projectId, todoId] = wsScopedBasecamp
      if (todoId) {
        onUrlChange(`basecamp-todo-${todoId}`, {
          type: 'integration-basecamp-todo',
          projectId,
          todoId,
        })
      } else {
        onUrlChange(`basecamp-project-${projectId}`, {
          type: 'integration-basecamp',
          projectId,
        })
      }
      return
    }

    // Extract Basecamp project ID from URL (supports /basecamp/:org/project/... )
    const { workspaceId: bcUrlWorkspace, strippedPath: basecampStrip } =
      stripOptionalBasecampWorkspaceSegment(pathname)
    const basecampMatch = basecampStrip.match(/^\/basecamp\/project\/(\d+)(?:\/todo\/(\d+))?/)
    if (basecampMatch) {
      const projectId = basecampMatch[1]
      const todoId = basecampMatch[2]
      
      if (todoId) {
        // Basecamp todo
        const basecampTodoId = `basecamp-todo-${todoId}`
        onUrlChange(basecampTodoId, {
          type: 'integration-basecamp-todo',
          projectId,
          todoId,
          ...(bcUrlWorkspace ? { workspaceId: bcUrlWorkspace } : {}),
        })
      } else {
        // Basecamp project
        const basecampProjectId = `basecamp-project-${projectId}`
        onUrlChange(basecampProjectId, {
          type: 'integration-basecamp',
          projectId,
          ...(bcUrlWorkspace ? { workspaceId: bcUrlWorkspace } : {}),
        })
      }
      return
    }

    // Workspace dashboard from URL (/workspace/:wsId)
    const workspaceHomeMatch = pathname.match(/^\/workspace\/(.+)$/)
    if (workspaceHomeMatch) {
      const wsId = decodeURIComponent(workspaceHomeMatch[1])
      const tabId = `workspace-home-${wsId}`
      onUrlChange(tabId, {
        type: 'workspace-home',
        workspaceId: wsId,
      })
      return
    }

    // Extract document ID from URL (/doc/... legacy)
    const docMatch = pathname.match(/^\/doc\/(.+)$/)
    if (docMatch) {
      const documentId = decodeURIComponent(docMatch[1])
      onUrlChange(documentId, {
        type: 'document'
      })
      return
    }

    // Extensionless document deep links (/{workspaceSlug}/path/to/doc)
    const urlIntent = parseAppPath(pathname, urlParams, [])
    if (urlIntent.type === 'document') {
      onUrlChange(urlIntent.pathOrId, { type: 'document' })
      return
    }

    // Extract Explorer path from URL
    const exploreMatch = pathname.match(/^\/explore(?:\/(.+))?$/)
    if (exploreMatch) {
      const folderPath = exploreMatch[1] ? decodeURIComponent(exploreMatch[1]) : null
      onUrlChange('list', {
        type: 'explorer',
        folderPath
      })
      return
    }
  }, []) // Empty deps - only run once on mount
}

/**
 * Hook to auto-open home tab when all tabs are closed
 */
export function useAutoOpenHomeTab(
  openTabs: OpenTab[],
  activeTabId: string | null,
  setOpenTabs: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void,
  setActiveTabId: (id: string | null) => void,
  navigation: ReturnType<typeof usePerTabNavigationHistory>,
  navigate: (path: string, options?: { replace?: boolean }) => void
) {
  useEffect(() => {
    // Never steal a cold document deep link back to Home.
    if (isDocumentDeepLinkRestorePending()) return
    if (openTabs.length === 0 && activeTabId !== 'home') {
      const homeTab: OpenTab = {
        id: 'home',
        title: 'Home',
        type: 'home' as const,
        isDirty: false,
        isPinned: false, // Explicitly unpinned by default
        tabSlotId: 'tab-main',
      }
      setOpenTabs([homeTab])
      setActiveTabId('home')
      navigation.navigate('tab-main', { documentId: null, viewMode: 'list' }, "auto-open-home-tab")
      navigate('/', { replace: true })
    }
  }, [openTabs.length, activeTabId, setOpenTabs, setActiveTabId, navigation, navigate])
}

/**
 * Keep the browser/OS tab title in sync with the active in-app tab (Maater brand suffix).
 * Bet: docs/product/bets/2026-07-07-browser-tab-title-sync.md
 */
export function useBrowserTabTitleSync(
  openTabs: OpenTab[],
  activeTabId: string | null,
  workspaces: WorkspaceRouteInfo[] = [],
) {
  useEffect(() => {
    const activeTab = openTabs.find((tab) => tab.id === activeTabId)
    document.title = browserTabTitleFromActiveTab(activeTab, BRAND.displayName, workspaces)
  }, [openTabs, activeTabId, workspaces])
}

